import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormsModule} from "@angular/forms";
import {MatCheckbox} from "@angular/material/checkbox";
import {Clipboard} from "@angular/cdk/clipboard";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import type {WorksModel} from "src/app/models/WorksModel";
import type {User} from "src/app/modules/auth/models/User";
import {PublicWorkService} from "src/app/modules/works/services/public-work.service";
import type {
  PublicWorkResponse,
  WorkShareChannel,
  WorkShareSettings,
} from "src/app/modules/works/interfaces/public-work.interface";
import {firstValueFrom, Subscription, timer} from "rxjs";
import {PublicWorkPreviewComponent} from "src/app/modules/works/components/public-work-preview/public-work-preview.component";
import {Capacitor} from "@capacitor/core";
import {Share} from "@capacitor/share";
import {ToastService} from "../../services/toast.service";
import {Router} from "@angular/router";
import {ModalService} from "../../services/modal.service";

@Component({
  selector: "app-external-work-share",
  templateUrl: "./external-work-share.component.html",
  styleUrl: "./external-work-share.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatCheckbox,
    TranslateModule,
    PublicWorkPreviewComponent,
  ],
})
export class ExternalWorkShareComponent implements OnChanges {
  @Input({required: true}) item!: WorksModel;
  @Input({required: true}) user!: User;
  @Input() comment = "";
  @Input() isMobile = false;
  @Output() commentRestored = new EventEmitter<string>();
  settings?: WorkShareSettings;
  includeDetails = false;
  private saving = false;
  private waiting = false;
  private commentRevision = 0;
  status = "";
  preview?: PublicWorkResponse;
  generated?: PublicWorkResponse;
  readonly nativeShareAvailable =
    Capacitor.isNativePlatform() || typeof navigator.share === "function";
  private sharing = false;
  private request?: Subscription;
  private debounce?: Subscription;
  private pendingUpdate = false;
  private savedMessage = "";
  private contextKey = "";

  constructor(
    private readonly router: Router,
    private readonly modal: ModalService,
    private readonly service: PublicWorkService,
    private readonly clipboard: Clipboard,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly toast: ToastService
  ) {}

  openSharedLinks(): void {
    if (this.busy) return;
    this.modal.closeModal();
    void this.router.navigate(["/recommendations/sent"], {
      queryParams: {filter: "links"},
    });
  }

  get busy(): boolean {
    return this.saving || this.waiting || this.sharing;
  }

  get title(): string {
    if (this.generated) return this.generated.social.title;
    return this.translate.instant("WORK_SHARE.SOCIAL_TITLE", {
      username: this.user.username,
      title: this.preview?.work.title ?? this.item.title,
    });
  }

  get hasNoPersonalDetails(): boolean {
    if (!this.includeDetails || !this.generated || this.busy) return false;
    const details = this.generated.share?.details;
    return (
      details?.rating === undefined && !details?.progress && !details?.message
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const contextKey = [
      this.item.type,
      this.item._id,
      this.user._id,
      this.user.visibility,
      this.language,
      this.country,
    ].join(":");
    if (this.contextKey !== contextKey) {
      this.contextKey = contextKey;
      this.request?.unsubscribe();
      this.debounce?.unsubscribe();
      this.saving = false;
      this.sharing = false;
      this.waiting = false;
      this.commentRevision = 0;
      this.settings = undefined;
      this.pendingUpdate = false;
      this.savedMessage = "";
      this.preview = undefined;
      this.generated = undefined;
      this.includeDetails = false;
      this.prepareLink(0, false);
    } else if (changes["comment"]) {
      this.commentRevision++;
      if (this.includeDetails && this.comment !== this.savedMessage)
        this.prepareLink(400);
    }
  }

  prepareLink(delay = 0, update = true, reactivate = false): void {
    this.debounce?.unsubscribe();
    this.waiting = !!delay;
    if (delay) {
      this.debounce = timer(delay)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.prepareLink(0, update, reactivate));
      return;
    }
    if (this.saving || this.sharing) {
      this.pendingUpdate ||= update;
      return;
    }
    this.status = "";
    this.generated = undefined;
    this.saving = true;
    const commentRevision = this.commentRevision;
    this.request = this.service
      .create(
        this.item.type,
        this.item._id,
        this.language,
        this.country,
        this.includeDetails,
        this.includeDetails,
        this.includeDetails ? this.comment : "",
        update,
        this.settings?.revision,
        reactivate
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.saving = false;
          this.generated = result;
          this.preview = result;
          this.settings = result.settings;
          this.savedMessage = result.share?.details?.message ?? "";
          if (!update) {
            this.includeDetails = result.settings?.includeDetails ?? false;
            const edited =
              this.commentRevision !== commentRevision || !!this.comment;
            if (!edited) this.commentRestored.emit(this.savedMessage);
            this.pendingUpdate ||=
              edited &&
              this.includeDetails &&
              this.comment !== this.savedMessage &&
              !result.settings?.revoked;
          }
          if (this.pendingUpdate) {
            this.pendingUpdate = false;
            this.prepareLink();
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.saving = false;
          this.waiting = false;
          this.debounce?.unsubscribe();
          this.pendingUpdate = false;
          this.status = "WORK_SHARE.CREATE_ERROR";
          this.cdr.markForCheck();
        },
      });
  }

  share(channel: WorkShareChannel): void {
    if (this.busy || !this.generated || !this.settings) return;
    void this.deliver(channel, this.generated.social.url);
  }

  copiedManually(event: ClipboardEvent): void {
    const input = event.target;
    if (
      !(input instanceof HTMLInputElement) ||
      input.selectionStart === input.selectionEnd
    )
      return;
    if (this.busy || !this.settings) {
      event.preventDefault();
      return;
    }
    void this.deliver("copy", input.value, true);
  }

  private get language(): string {
    return this.user.language?.startsWith("fr") ? "fr" : "en";
  }

  private get country(): string {
    return this.user.country ?? (this.language === "fr" ? "FR" : "US");
  }

  private async deliver(
    channel: WorkShareChannel,
    url: string,
    copied = false
  ): Promise<void> {
    const settings = this.settings;
    if (!settings) return;
    const contextKey = this.contextKey;
    let delivered = copied;
    this.sharing = true;
    try {
      if (channel !== "native") await this.activate(settings);
      if (this.destroyRef.destroyed || this.contextKey !== contextKey) return;
      if (channel === "copy") {
        this.copyLink(url, copied);
      } else if (channel === "native") {
        await this.sendNative(url);
        delivered = true;
        await this.activate(settings);
        if (this.contextKey === contextKey) this.status = "";
      } else {
        this.launchChannel(channel, url);
      }
    } catch (error: unknown) {
      if (this.destroyRef.destroyed || this.contextKey !== contextKey) return;
      this.handleShareError(error, delivered);
    } finally {
      if (this.contextKey === contextKey) this.finishSharing();
    }
    if (!this.destroyRef.destroyed) this.cdr.markForCheck();
  }

  private copyLink(url: string, copied: boolean): void {
    if (!copied && !this.clipboard.copy(url)) {
      this.status = "WORK_SHARE.COPY_ERROR";
      return;
    }
    this.status = "";
    this.toast.sendToast({
      text: "WORK_SHARE.COPIED",
      classes: "bg-form-success",
    });
  }

  private sendNative(url: string): Promise<unknown> {
    return Capacitor.isNativePlatform()
      ? Share.share({url})
      : navigator.share({url});
  }

  private launchChannel(
    channel: Exclude<WorkShareChannel, "copy" | "native">,
    url: string
  ): void {
    const destinations = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(url)}`,
      sms: `sms:?body=${encodeURIComponent(url)}`,
      email: `mailto:?body=${encodeURIComponent(url)}`,
    };
    this.status =
      channel === "sms" && this.clipboard.copy(url)
        ? "WORK_SHARE.SMS_READY"
        : "WORK_SHARE.CONTINUE_SHARING";
    globalThis.location.assign(destinations[channel]);
  }

  private handleShareError(error: unknown, delivered: boolean): void {
    if (
      error instanceof Error &&
      (error.name === "AbortError" || error.message === "Share canceled")
    ) {
      this.status = "";
      return;
    }
    if (!delivered) {
      this.generated = undefined;
      this.settings = undefined;
    }
    this.status = delivered
      ? "WORK_SHARE.ACTIVATE_ERROR"
      : "WORK_SHARE.COPY_ERROR";
  }

  private finishSharing(): void {
    this.sharing = false;
    if (!this.pendingUpdate || this.destroyRef.destroyed) return;
    this.pendingUpdate = false;
    this.prepareLink();
  }

  private async activate(settings: WorkShareSettings): Promise<void> {
    if (settings.revoked || !settings.draft || this.destroyRef.destroyed)
      return;
    await firstValueFrom(
      this.service
        .activate(settings.id, settings.revision)
        .pipe(takeUntilDestroyed(this.destroyRef))
    );
    settings.draft = false;
  }
}
